import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export type ImageFrameFieldProps = {
  /** Current image (data URL or saved URL). Empty shows `emptyContent`. */
  value: string;
  /** Receives the framed image as a data URL, or "" when removed. */
  onChange: (dataUrl: string) => void;
  /** Frame width / height, e.g. 16 / 9 for a banner or 1 for a portrait. */
  aspectRatio: number;
  /** Pixel width of the exported crop. Height follows the aspect ratio. */
  outputWidth: number;
  /** Upload prompt shown while there is no image. */
  emptyContent: React.ReactNode;
  /** Opens the file picker of the surrounding upload control. */
  onReplace: () => void;
  /** Optional remove control; the design banner shows "Delete Banner". */
  onRemove?: () => void;
  removeLabel?: string;
  /** Label of the replace control; the design banner uses "Update Banner". */
  replaceLabel?: string;
  /** Banner-style controls stay visible; image slots show them on hover. */
  alwaysShowControls?: boolean;
  /** "small" = Claude Design image-slot controls (11 px, 5 px 10 px, dark
   * translucent, 8 px from the top right) for small frames such as the
   * speaker image and partner logo, so the buttons fit inside the frame. */
  controlSize?: "default" | "small";
  /** Accessible name for the image. */
  alt: string;
  className?: string;
  radius?: number;
  /** Centred dashed boxes shown while editing, e.g. the parts of a banner
   * that a page shows on desktop and on a phone. Fractions of the frame. */
  cropGuides?: readonly ImageFrameCropGuide[];
};

export type ImageFrameCropGuide = {
  label: string;
  widthFraction: number;
  heightFraction: number;
};

type FrameView = { scale: number; x: number; y: number };

const MIN_SCALE = 1;
const MAX_SCALE = 4;

const clampView = (
  view: FrameView,
  frame: { width: number; height: number },
  natural: { width: number; height: number },
): FrameView => {
  const base = Math.max(frame.width / natural.width, frame.height / natural.height);
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, view.scale));
  const drawnWidth = natural.width * base * scale;
  const drawnHeight = natural.height * base * scale;
  // Keep the frame covered: the image edge may never move inside the frame.
  const x = Math.min(0, Math.max(frame.width - drawnWidth, view.x));
  const y = Math.min(0, Math.max(frame.height - drawnHeight, view.y));
  return { scale, x, y };
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    // Saved images come from S3; anonymous CORS keeps the canvas exportable.
    if (!src.startsWith("data:") && !src.startsWith("blob:")) {
      image.crossOrigin = "anonymous";
    }
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("The image could not be loaded."));
    image.src = src;
  });

const exportFramedImage = (
  image: HTMLImageElement,
  source: string,
  outputWidth: number,
  outputHeight: number,
  draw: { x: number; y: number; width: number; height: number },
): string => {
  const canvas = document.createElement("canvas");
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas unavailable");
  context.drawImage(image, draw.x, draw.y, draw.width, draw.height);
  return source.startsWith("data:image/png")
    ? canvas.toDataURL("image/png")
    : canvas.toDataURL("image/jpeg", 0.9);
};

/** Centre "cover" crop of a new upload at the frame shape, so the saved image
 * is the one the frame shows (not the raw original) until the user reframes. */
export const frameImageAtCentre = async (
  source: string,
  aspectRatio: number,
  outputWidth: number,
): Promise<string> => {
  const image = await loadImage(source);
  const outputHeight = Math.round(outputWidth / aspectRatio);
  const scale = Math.max(outputWidth / image.naturalWidth, outputHeight / image.naturalHeight);
  const width = image.naturalWidth * scale;
  const height = image.naturalHeight * scale;
  return exportFramedImage(image, source, outputWidth, outputHeight, {
    x: (outputWidth - width) / 2,
    y: (outputHeight - height) / 2,
    width,
    height,
  });
};

// Claude Design image-slot behaviour for uploaded images: Replace and Edit
// controls on a filled frame; Edit lets the user drag to move and scroll,
// use a corner handle, or the zoom slider to scale; Done, Escape, or a click
// outside commits the framed crop as a new image.
export const ImageFrameField = ({
  value,
  onChange,
  aspectRatio,
  outputWidth,
  emptyContent,
  onReplace,
  onRemove,
  removeLabel = "Remove",
  replaceLabel = "Replace",
  alwaysShowControls = false,
  controlSize = "default",
  alt,
  className = "",
  radius = 8,
  cropGuides = [],
}: ImageFrameFieldProps) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(false);
  const [source, setSource] = useState(value);
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const [frame, setFrame] = useState({ width: 0, height: 0 });
  const [view, setView] = useState<FrameView>({ scale: 1, x: 0, y: 0 });
  const [error, setError] = useState<string | null>(null);
  const lastCommitted = useRef(value);

  // A new upload (not our own commit) becomes the editable source.
  useEffect(() => {
    if (value !== lastCommitted.current) {
      lastCommitted.current = value;
      setSource(value);
      setEditing(false);
      setError(null);
    }
  }, [value]);

  useLayoutEffect(() => {
    const node = frameRef.current;
    if (!node) return undefined;
    const measure = () =>
      setFrame({ width: node.clientWidth, height: node.clientHeight });
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [value]);

  const startEditing = () => {
    setError(null);
    loadImage(source)
      .then((image) => {
        const size = { width: image.naturalWidth, height: image.naturalHeight };
        setNatural(size);
        const base = Math.max(frame.width / size.width, frame.height / size.height);
        setView(
          clampView(
            {
              scale: 1,
              x: (frame.width - size.width * base) / 2,
              y: (frame.height - size.height * base) / 2,
            },
            frame,
            size,
          ),
        );
        setEditing(true);
      })
      .catch(() => setError(`We cannot move or zoom this image. Click ${replaceLabel} to upload a new image.`));
  };

  const commit = useCallback(async () => {
    if (!natural || !frame.width) {
      setEditing(false);
      return;
    }
    try {
      const image = await loadImage(source);
      const outputHeight = Math.round(outputWidth / aspectRatio);
      const ratio = outputWidth / frame.width;
      const base = Math.max(frame.width / natural.width, frame.height / natural.height);
      const framed = exportFramedImage(image, source, outputWidth, outputHeight, {
        x: view.x * ratio,
        y: view.y * ratio,
        width: natural.width * base * view.scale * ratio,
        height: natural.height * base * view.scale * ratio,
      });
      lastCommitted.current = framed;
      onChange(framed);
      setEditing(false);
    } catch {
      setError(`We cannot move or zoom this image. Click ${replaceLabel} to upload a new image.`);
      setEditing(false);
    }
  }, [aspectRatio, frame, natural, onChange, outputWidth, replaceLabel, source, view]);

  useEffect(() => {
    if (!editing) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") void commit();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (frameRef.current && !frameRef.current.parentElement?.contains(event.target as Node)) {
        void commit();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [commit, editing]);

  const updateView = (next: FrameView) => {
    if (natural) setView(clampView(next, frame, natural));
  };

  // Scaling keeps the frame centre fixed so the subject stays in view.
  const scaleAroundCentre = (nextScale: number) => {
    if (!natural) return;
    const bounded = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
    const centreX = frame.width / 2;
    const centreY = frame.height / 2;
    // Functional update: pointer drags call this many times per render.
    setView((current) => {
      const factor = bounded / current.scale;
      return clampView(
        {
          scale: bounded,
          x: centreX - (centreX - current.x) * factor,
          y: centreY - (centreY - current.y) * factor,
        },
        frame,
        natural,
      );
    });
  };

  const onPanStart = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!editing) return;
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const origin = view;
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);
    const move = (moveEvent: PointerEvent) =>
      updateView({
        scale: origin.scale,
        x: origin.x + moveEvent.clientX - startX,
        y: origin.y + moveEvent.clientY - startY,
      });
    const end = () => {
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", end);
      target.removeEventListener("pointercancel", end);
    };
    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", end);
    target.addEventListener("pointercancel", end);
  };

  const onHandleStart = (event: React.PointerEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    const centre = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    const startDistance = Math.hypot(event.clientX - centre.x, event.clientY - centre.y) || 1;
    const startScale = view.scale;
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);
    const move = (moveEvent: PointerEvent) => {
      const distance = Math.hypot(moveEvent.clientX - centre.x, moveEvent.clientY - centre.y);
      scaleAroundCentre(startScale * (distance / startDistance));
    };
    const end = () => {
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", end);
      target.removeEventListener("pointercancel", end);
    };
    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", end);
    target.addEventListener("pointercancel", end);
  };

  if (!value) return <>{emptyContent}</>;

  const base = natural ? Math.max(frame.width / natural.width, frame.height / natural.height) : 1;
  const small = controlSize === "small";
  // Design banner buttons: 12px/600, 7px 14px, 6px radius, soft shadow.
  // Small frames: CMP Dashboard (15) image-slot.js `.ctl button` (11px,
  // 5px 10px, rgba(0,0,0,.65), hover .8).
  const controlClassName = small
    ? "whitespace-nowrap rounded-[6px] border-0 bg-black/65 px-[10px] py-[5px] text-[11px] leading-none text-white backdrop-blur-[6px] hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cpaGold"
    : "rounded-[6px] border-0 px-[14px] py-[7px] text-[12px] font-semibold text-white shadow-[0_2px_6px_rgba(0,0,0,0.25)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-cpaGold";

  return (
    <div className={className}>
      <div
        ref={frameRef}
        className={`group relative w-full touch-none select-none overflow-hidden bg-[#F7F7F8] ${editing ? "cursor-move outline outline-2 outline-dashed outline-cpaGold -outline-offset-2" : ""}`}
        style={{ aspectRatio: String(aspectRatio), borderRadius: radius }}
        onPointerDown={onPanStart}
        onWheel={(event) => {
          if (!editing) return;
          event.preventDefault();
          scaleAroundCentre(view.scale * (event.deltaY < 0 ? 1.08 : 1 / 1.08));
        }}
        onDoubleClick={() => {
          if (!editing) startEditing();
        }}
      >
        {editing && natural ? (
          <img
            src={source}
            alt={alt}
            draggable={false}
            className="pointer-events-none absolute left-0 top-0 max-w-none"
            style={{
              width: natural.width * base * view.scale,
              height: natural.height * base * view.scale,
              transform: `translate(${view.x}px, ${view.y}px)`,
            }}
          />
        ) : (
          <img src={value} alt={alt} draggable={false} className="h-full w-full object-cover" />
        )}
        {editing
          ? cropGuides.map((guide) => (
              <span
                key={guide.label}
                aria-hidden="true"
                className="pointer-events-none absolute border-2 border-dashed border-white/90 shadow-[0_0_0_1px_rgba(0,0,0,0.35)]"
                style={{
                  width: `${guide.widthFraction * 100}%`,
                  height: `${guide.heightFraction * 100}%`,
                  left: `${((1 - guide.widthFraction) / 2) * 100}%`,
                  top: `${((1 - guide.heightFraction) / 2) * 100}%`,
                }}
              >
                <span className="absolute left-[4px] top-[4px] rounded-[4px] bg-black/60 px-[6px] py-[2px] text-[11px] font-semibold text-white">
                  {guide.label}
                </span>
              </span>
            ))
          : null}
        {editing
          ? (["nw", "ne", "sw", "se"] as const).map((corner) => (
              <span
                key={corner}
                aria-hidden="true"
                onPointerDown={onHandleStart}
                className="absolute h-[14px] w-[14px] rounded-full border-2 border-white bg-cpaGold shadow"
                style={{
                  cursor: corner === "nw" || corner === "se" ? "nwse-resize" : "nesw-resize",
                  left: corner.endsWith("w") ? 6 : undefined,
                  right: corner.endsWith("e") ? 6 : undefined,
                  top: corner.startsWith("n") ? 6 : undefined,
                  bottom: corner.startsWith("s") ? 6 : undefined,
                }}
              />
            ))
          : null}
        <div
          className={`absolute ${small ? "right-[8px] top-[8px] gap-[6px]" : "right-[10px] top-[10px] gap-[8px]"} flex transition-opacity ${editing || alwaysShowControls ? "opacity-100" : "opacity-0 group-focus-within:opacity-100 group-hover:opacity-100"}`}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <button type="button" className={small ? controlClassName : `${controlClassName} bg-[#E1A731] hover:bg-[#C8912A]`} onClick={onReplace}>
            {replaceLabel}
          </button>
          <button
            type="button"
            className={small ? controlClassName : `${controlClassName} bg-[#0A0A0A]/80 hover:bg-[#0A0A0A]`}
            aria-pressed={editing}
            onClick={() => (editing ? void commit() : startEditing())}
          >
            {editing ? "Done" : "Edit"}
          </button>
          {onRemove && !editing ? (
            <button type="button" className={`${controlClassName} bg-[#F21B1B] hover:bg-[#D01414]`} onClick={onRemove}>
              {removeLabel}
            </button>
          ) : null}
        </div>
      </div>
      {editing ? (
        <div className="mt-2 flex items-center gap-3 text-[12px] text-[#3A3A3A]">
          <span>Drag to move. Scroll or use a corner to zoom.</span>
          <input
            type="range"
            aria-label="Zoom image"
            min={MIN_SCALE}
            max={MAX_SCALE}
            step={0.01}
            value={view.scale}
            onChange={(event) => scaleAroundCentre(Number(event.target.value))}
            className="ml-auto w-[140px] accent-[#E1A731]"
          />
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default ImageFrameField;
